import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { Box, Button, Checkbox, Container, Image, Typography } from "src/UILibrary"
import * as yup from "yup"
import { ShowHeader } from "../../../states/showHeader"
import { useRecoilState } from "recoil"
import InputNameField from "src/components/atom/InputNameField"
import InputTextField from "src/components/atom/InputTextField"
import SelectField from "src/components/atom/SelectField"
import { ShowToast } from "src/states/showToast"
import { useLocation, useNavigate } from "react-router-dom"
import { validateFields } from "src/modules/validation"
import { ProfileService } from "src/openapi"
import { RESPONSE_CODE, SNACKBAR_TYPES } from "src/modules/const"
import { useLoginContext } from "src/hooks/useLoginContext"
import LogoPNG from "src/assets/icons/logo.png"
import { useMediaQuery } from "react-responsive"
import { ShowProgress } from "src/states/showProgress"

export const SignupAdministrator = () => {
  const { t } = useTranslation("translation", { keyPrefix: "signup" })

  const isPC = useMediaQuery({ query: "(min-width: 720px)" })

  const [, setShowHeader] = useRecoilState(ShowHeader)
  const [, setShowToast] = useRecoilState(ShowToast)
  const [, setShowProgress] = useRecoilState(ShowProgress)

  const { areas } = useLoginContext()

  const navigate = useNavigate()

  const { state } = useLocation()
  const { userId } = state || ""
  const { email } = state || ""

  if (!userId || !email) {
    navigate("/signup/type")
  }

  useEffect(() => {
    setShowHeader(false)
  })

  const [firstName, setFirstName] = useState<string>("")
  const [lastName, setLastName] = useState<string>("")
  const [firstNameJP, setFirstNameJP] = useState<string>("")
  const [lastNameJP, setLastNameJP] = useState<string>("")
  const [areaID, setAreaID] = useState<number>(1)
  const [agreeTerm, setAgreeTerm] = useState<boolean>(false)
  const [agreePolicy, setAgreePolicy] = useState<boolean>(false)
  const [nameValidationMessage, setNameValidationMessage] = useState<string>("")

  const validate = async () => {
    let validation = true

    try {
      await validateFields(
        yup.object().shape({
          firstName: yup.string().required(t("please_input_first_name")),
          lastName: yup.string().required(t("please_input_last_name")),
          firstNameJP: yup
            .string()
            .required(t("please_input_first_name_jp"))
            .matches(/^[ァ-ヶｱ-ﾝﾞﾟ一-]*$/, t("please_input_jp_string")),
          lastNameJP: yup
            .string()
            .required(t("please_input_last_name_jp"))
            .matches(/^[ァ-ヶｱ-ﾝﾞﾟ一-]*$/, t("please_input_jp_string")),
        }),
        ["firstName", "lastName", "firstNameJP", "lastNameJP"],
        [firstName, lastName, firstNameJP, lastNameJP]
      )
      validation = validation && true
      setNameValidationMessage("")
    } catch (err: any) {
      setNameValidationMessage(err.message)
      validation = validation && false
    }

    if (!agreeTerm) {
      setShowToast({ show: true, text: t("please_confirm_term"), type: SNACKBAR_TYPES.WARNING })
      return false
    }

    if (!agreePolicy) {
      setShowToast({ show: true, text: t("please_confirm_policy"), type: SNACKBAR_TYPES.WARNING })
      return false
    }

    return validation
  }

  const handleRegister = async () => {
    if (!(await validate())) {
      return false
    }

    setShowProgress(true)
    ProfileService.createAdmin({
      userId: userId,
      email: email,
      firstName: firstName,
      lastName: lastName,
      firstNameKana: firstNameJP,
      lastNameKana: lastNameJP,
      areaId: areaID,
      comment: "ADMIN_REGISTER",
    })
      .then((res) => {
        setShowProgress(false)
        if (res.status === RESPONSE_CODE.CREATE_SUCCESS) {
          setShowToast({ show: true, text: t("success"), type: SNACKBAR_TYPES.SUCCESS })
          navigate("/")
        } else {
          setShowToast({ show: true, text: t("failed"), type: SNACKBAR_TYPES.ERROR })
        }
      })
      .catch(() => {
        setShowProgress(false)
        setShowToast({ show: true, text: t("failed"), type: SNACKBAR_TYPES.ERROR })
      })
  }

  return (
    <>
      <Container maxWidth="lg">
        <Image
          src={LogoPNG}
          imageStyle={{ width: "auto", height: "100%", mx: "auto" }}
          sx={{ height: "80px", cursor: "pointer", display: "flex" }}
          onClick={() => navigate("/")}
        />
        <Typography.Large
          color="textPrimary"
          sx={{
            my: "40px",
          }}
        >
          {t("signup_administrator")}
        </Typography.Large>

        <Box maxWidth="md" sx={{ mx: "auto" }}>
          <InputNameField
            name={t("name")}
            required
            firstName={firstName}
            lastName={lastName}
            firstNameJP={firstNameJP}
            lastNameJP={lastNameJP}
            handleFirstName={setFirstName}
            handleLastName={setLastName}
            handleFirstNameJP={setFirstNameJP}
            handleLastNameJP={setLastNameJP}
            validationMessage={nameValidationMessage}
          ></InputNameField>
          <SelectField
            name={t("address")}
            required
            sx={{ mt: "40px", mb: "40px", width: "200px" }}
            selected={areaID}
            handleSelected={setAreaID}
            datas={areas}
          ></SelectField>
          <InputTextField
            name={t("term")}
            sx={{ mt: "40px" }}
            inputStyle={{ width: isPC ? "690px" : "100%", color: "gray" }}
            text={t("term_text")}
            required
            multiline
            readOnly
            handleText={() => {}}
          />
          <Checkbox
            label={t("agree")}
            checked={agreeTerm}
            onChange={(event, checked) => setAgreeTerm(checked)}
          />
          <InputTextField
            name={t("policy")}
            sx={{ mt: "40px" }}
            inputStyle={{ width: isPC ? "690px" : "100%", color: "gray" }}
            text={t("policy_text")}
            required
            multiline
            readOnly
            handleText={() => {}}
          />
          <Checkbox
            label={t("agree")}
            checked={agreePolicy}
            onChange={(event, checked) => setAgreePolicy(checked)}
          />
          <Button
            variant="contained"
            sx={{
              minWidth: "240px",
              py: "16px",
              px: "88px",
              fontSize: "1rem",
              fontFamily: "Noto Sans JP !important",
              lineHeight: "1rem",
              fontWeight: 700,
              my: "80px",
              mx: "auto",
              display: "flex",
              justifyContent: "center",
              borderRadius: 0,
              ":hover": {
                bgcolor: "#2DA389",
              },
            }}
            onClick={() => handleRegister()}
          >
            {t("register")}
          </Button>
        </Box>
      </Container>
    </>
  )
}
